import { useState } from 'react';
import { hasScope } from '@nyce/admin-kit/api';
import { Button, Chip, Dialog } from '@nyce/admin-kit/ui';
import { AuthApiError, type Tenant } from '../../../identity/api';
import { SCOPES } from '../../../api';
import { useHubLogin } from '../../../session';
import { activeTargetInfo, client, scim, type ScimEndpoint } from '../../../identity/session';
import { SecretReveal } from '@nyce/admin-kit/console';
import { ErrorBox, Loading, useAsync } from '../../../components/common';
import { authProvisioningEndpoint, scimTenantUrl } from '../ScimEndpoints';

type Notify = { success: (t: string) => void; error: (t: string) => void };

/** A labelled value with a copy button (keys shown once, URLs to paste elsewhere). */
export function CopyRow({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <div className="nyk-ui-field">
      <span>{label}</span>
      <SecretReveal value={value} data-testid={testId} />
    </div>
  );
}

/**
 * SCIM from Entra ID into this Database tenant, in one step: issue a provisioning key (revoking the current one),
 * point the environment's SCIM gateway at this tenant's AUTH provisioning API, and show what to paste into Entra
 * (Tenant URL + Secret Token, the key shown once).
 */
export function ScimConnection({
  tenant,
  canWrite,
  notify,
  onKeyIssued,
}: {
  tenant: Tenant;
  canWrite: boolean;
  notify: Notify;
  onKeyIssued: () => void;
}) {
  const hub = useHubLogin();
  const info = activeTargetInfo();
  const canRead = hasScope(hub?.scopes, SCOPES.scimRead) && !!info?.scimUrl;
  const canConnect = canWrite && hasScope(hub?.scopes, SCOPES.scimWrite) && !!info?.scimUrl;
  const row = useAsync<ScimEndpoint | null>(
    () =>
      canRead
        ? scim.get(tenant.id).catch((e) => {
            if (e instanceof AuthApiError && e.status === 404) return null;
            throw e;
          })
        : Promise.resolve(null),
    [tenant.id, canRead]
  );
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ key: string; gatewayError: string | null } | null>(null);
  if (tenant.identityProvider !== 'Database' || !info?.scimUrl || !canRead) return null;

  const expected = authProvisioningEndpoint(info.authUrl, tenant.id);
  const current = row.data;
  const connected = current?.target === 'auth' && current.endpoint === expected;

  const connect = async () => {
    setBusy(true);
    try {
      const key = (await client.tenants.provisioningKeys.issue(tenant.id)).provisioning_key;
      onKeyIssued();
      let gatewayError: string | null = null;
      try {
        await scim.put(tenant.id, { endpoint: expected, target: 'auth', trace: current?.trace ?? false });
      } catch (e) {
        gatewayError = (e as Error).message;
      }
      setResult({ key, gatewayError });
      setConfirming(false);
      await row.reload();
      if (gatewayError) notify.error(`Key issued, but the gateway was not updated: ${gatewayError}`);
      else notify.success(`SCIM connected for ${tenant.name}`);
    } catch (e) {
      notify.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="nyk-card" data-testid="scim-connection">
      <div className="nyk-card-head">
        <h2>SCIM from Entra ID</h2>
        {canConnect && (
          <Button size="sm" onClick={() => setConfirming(true)}>
            {connected ? 'Reconnect (new key)' : 'Connect SCIM'}
          </Button>
        )}
      </div>
      <ErrorBox error={row.error} />
      <Loading loading={row.loading && row.data === null && !row.error} />
      {!row.loading && (
        <p className="nyk-card-intro">
          {connected ? (
            <>
              <Chip size="sm" className="nyk-tone-green">
                connected
              </Chip>{' '}
              The {info.label} gateway forwards this tenant's provisioning to AUTH.
            </>
          ) : current ? (
            <>
              <Chip size="sm" className="nyk-tone-amber">
                elsewhere
              </Chip>{' '}
              The gateway sends this tenant to <code>{current.endpoint}</code> ({current.target}), not to this
              AUTH.
            </>
          ) : (
            <>Not connected: the {info.label} gateway has no endpoint for this tenant.</>
          )}{' '}
          Entra Tenant URL: <code>{scimTenantUrl(info.scimUrl, tenant.id)}</code>
        </p>
      )}
      {confirming && (
        <Dialog
          open
          onClose={() => setConfirming(false)}
          title={`Connect SCIM for ${tenant.name}`}
          maxWidth={600}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
              <Button onClick={() => void connect()} loading={busy}>
                Issue key and connect
              </Button>
            </>
          }
        >
          <ol className="nyk-card-intro">
            <li>Issues a new provisioning key; the current one stops working at once.</li>
            <li>
              Points the {info.label} SCIM gateway at <code>{expected}</code>.
            </li>
            <li>Shows the Tenant URL and the key (once) for the Entra ID provisioning settings.</li>
          </ol>
        </Dialog>
      )}
      {result && (
        <Dialog
          open
          onClose={() => setResult(null)}
          title="Entra ID provisioning settings"
          maxWidth={680}
          footer={
            <Button variant="secondary" onClick={() => setResult(null)}>
              I have stored the key
            </Button>
          }
        >
          {result.gatewayError && <ErrorBox error={`Gateway not updated: ${result.gatewayError}`} />}
          <p className="nyk-card-intro">
            Copy the Secret Token now — it is not stored and cannot be shown again.
          </p>
          <div className="nyk-form">
            <div className="nyk-span2">
              <CopyRow
                label="Tenant URL"
                value={scimTenantUrl(info.scimUrl, tenant.id)}
                testId="scim-tenant-url"
              />
            </div>
            <div className="nyk-span2">
              <CopyRow label="Secret Token" value={result.key} testId="scim-secret" />
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}
